import { useRef, useState, useEffect } from 'react';
import { SonicPreset, DEFAULT_PRESET, AudioSettings } from '../types';

import type { ToneAudioService } from '../services/audio/ToneAudioService';

// Define a type for AudioEngine to avoid static import
type AudioEngineType = ToneAudioService;

export const useAudioController = (
    preset: SonicPreset,
    settings: AudioSettings
) => {
    const audioEngine = useRef<AudioEngineType | null>(null);
    const [isAudioStarted, setIsAudioStarted] = useState(false);
    const [isSceneChanging, setIsSceneChanging] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    // We no longer initialize in useEffect to keep the service out of the main bundle
    // It will be loaded on demand in handleStartAudio

    useEffect(() => {
        if (audioEngine.current) {
            setIsLoading(true);
            audioEngine.current.updatePreset(preset).then(() => {
                setIsLoading(false);
            }).catch(err => {
                console.error("Preset update failed:", err);
                setIsLoading(false);
            });
        }
    }, [preset]);

    // OPTIMIZATION: Consolidated settings sync into a single effect
    // Instead of 10 separate useEffects, we batch all settings updates together
    useEffect(() => {
        const engine = audioEngine.current;
        if (!engine) return;

        engine.setMasterGain(settings.volume / 100);
        engine.setReverbLevel(settings.reverb / 100);
        engine.setDelayLevel(settings.delay / 100);
        engine.setPlayMode(settings.playMode);
        engine.setInteractionMode(settings.interactionMode);
        engine.setMasterTuning(settings.masterTuning);
        engine.setBreathingMode(settings.breathingMode);
        engine.setLoFiMode(settings.lofiMode);
        engine.setBinauralMode(settings.binauralMode);
        engine.setIdleSoundsEnabled(settings.idleSoundsEnabled);
    }, [settings]);

    const handleStartAudio = async (onComplete?: () => void) => {
        try {
            console.log('[useAudioController] handleStartAudio called');

            // Dynamic import of AudioEngine
            if (!audioEngine.current) {
                console.log('[useAudioController] Dynamically importing ToneAudioService...');
                const { ToneAudioService } = await import('../services/audio/ToneAudioService');
                audioEngine.current = new ToneAudioService(preset);
                console.log('[useAudioController] ToneAudioService instantiated');
            }

            if (audioEngine.current) {
                // CRITICAL: init() and resume() MUST happen within the user gesture callstack.
                // Do NOT put these inside a setTimeout.
                console.log('[useAudioController] Calling init()...');
                await audioEngine.current.init();
                console.log('[useAudioController] Calling resume()...');
                await audioEngine.current.resume();

                console.log('[useAudioController] Setting isSceneChanging = true');
                setIsSceneChanging(true);

                // Allow time for fade-to-black before switching scenes
                setTimeout(() => {
                    try {
                        console.log('[useAudioController] setTimeout callback - setting levels...');
                        // Set initial levels
                        audioEngine.current!.setMasterGain(settings.volume / 100);
                        audioEngine.current!.setReverbLevel(settings.reverb / 100);
                        audioEngine.current!.setDelayLevel(settings.delay / 100);
                        audioEngine.current!.setPlayMode(settings.playMode);
                        audioEngine.current!.setInteractionMode(settings.interactionMode);
                        audioEngine.current!.setMasterTuning(settings.masterTuning);
                        audioEngine.current!.setBreathingMode(settings.breathingMode);
                        audioEngine.current!.setLoFiMode(settings.lofiMode);
                        audioEngine.current!.setBinauralMode(settings.binauralMode);
                        audioEngine.current!.setIdleSoundsEnabled(settings.idleSoundsEnabled);

                        console.log('[useAudioController] Setting isAudioStarted = true');
                        setIsAudioStarted(true);

                        // Brief delay before fading back in to ensure mounting is complete
                        setTimeout(() => {
                            console.log('[useAudioController] Final setTimeout - clearing sceneChanging');
                            setIsSceneChanging(false);
                            if (typeof onComplete === 'function') {
                                onComplete();
                            }
                        }, 100);
                    } catch (innerErr) {
                        console.error('[useAudioController] Error in first setTimeout:', innerErr);
                    }
                }, 500);
            }
        } catch (err) {
            console.error('[useAudioController] Error in handleStartAudio:', err);
        }
    };

    const stopAudio = () => {
        setIsSceneChanging(true);
        // Short delay for the fade-to-black to complete before switching states
        setTimeout(() => {
            setIsAudioStarted(false);
            setIsSceneChanging(false);
        }, 500);
    }

    return {
        audioEngine,
        isAudioStarted,
        isLoading,
        isSceneChanging,
        handleStartAudio,
        stopAudio
    };
};
